import { useEffect, useRef, useState } from "react";
import { getTranslate } from "react-localize-redux";
import { useSelector } from "react-redux";

import { closeCasinoProviderModal } from "@pages/casino/utils/bettoCasinoEvents";

import { getProviderLabel } from "./utils";

const selectTranslate = (state) => ({
	translate: getTranslate(state.localize),
});

const useController = ({
	items,
	value,
	onSelect,
	onClose,
	listenForOpenEvent = false,
	onEventOpen,
}) => {
	const searchInputRef = useRef(null);
	const { translate } = useSelector(selectTranslate);

	const [open, setOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [isSearchFocused, setIsSearchFocused] = useState(false);

	const filteredItems =
		items?.filter((item) => {
			if (!searchTerm) return true;

			const term = searchTerm.toLowerCase();
			const label = getProviderLabel(item).toLowerCase();

			return (
				label.includes(term) ||
				String(item?.key ?? "")
					.toLowerCase()
					.includes(term) ||
				String(item?.value ?? "")
					.toLowerCase()
					.includes(term)
			);
		}) ?? [];

	useEffect(() => {
		onClose?.(open);
	}, [open, onClose]);

	useEffect(() => {
		if (!open) {
			setSearchTerm("");
			setIsSearchFocused(false);
			return undefined;
		}

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				setOpen(false);
				closeCasinoProviderModal();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open]);

	useEffect(() => {
		if (!listenForOpenEvent) return undefined;

		const openByEvent = (event) => {
			if (event?.detail?.open === false) {
				setOpen(false);
				return;
			}

			onEventOpen?.(event?.detail);
			setOpen(true);
		};

		window.addEventListener("openCasinoProviderModal", openByEvent);
		return () => window.removeEventListener("openCasinoProviderModal", openByEvent);
	}, [listenForOpenEvent, onEventOpen]);

	const handleOpen = () => setOpen(true);

	const handleClose = () => {
		setOpen(false);
		closeCasinoProviderModal();
	};

	const handleSelect = (item) => {
		setOpen(false);
		closeCasinoProviderModal();

		if (item?.key && item.key === value?.key) {
			onSelect?.(null);
			return;
		}

		onSelect?.(item);
	};

	const handleClearSelection = (event) => {
		event?.stopPropagation();
		setOpen(false);
		closeCasinoProviderModal();
		onSelect?.(null);
	};

	const handleClearSearch = () => {
		setSearchTerm("");
		searchInputRef.current?.focus();
	};

	return {
		refs: { searchInputRef },
		helpers: { translate },
		state: {
			open,
			searchTerm,
			isSearchFocused,
			filteredItems,
			isProviderActive: Boolean(value?.key),
			selectedKey: value?.key,
			totalCount: items?.length ?? 0,
		},
		handlers: {
			handleOpen,
			handleClose,
			handleSelect,
			handleClearSelection,
			handleClearSearch,
			setSearchTerm,
			setIsSearchFocused,
		},
	};
};

export default useController;
